import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/store/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import { trackSignUp, initAnalytics } from '@/lib/analytics';
import { RouterLink } from '@/components/router/router-link';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { authControllerSignup, authControllerGoogleSignin, authControllerGetOnboardingStatus } from '@/apis/backend/generated/auth/auth';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { CheckEmailConfirmation } from '@/components/auth/CheckEmailConfirmation';
import { LegalConsentNotice } from '@/components/auth/LegalConsentNotice';
import { useShopifyAppStoreControllerGetInstallState } from '@/apis/backend/generated/integrations-shopify-app-store/integrations-shopify-app-store';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Link,
  Alert,
  Stack,
  Button,
  Divider,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';

export const Route = createFileRoute('/auth/sign-up')({
  validateSearch: (search: Record<string, unknown>) => ({
    shopify_state: search.shopify_state as string | undefined,
    code: search.code as string | undefined,
    aff: search.aff as string | undefined,
    redirect: search.redirect as string | undefined,
  }),
  component: SignUpPage,
});

function SignUpPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { shopify_state: shopifyState, code: promoCode, aff: affiliateCode, redirect: redirectTo } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Fetch Shopify install state if token is present
  const { data: shopifyInstallState, isLoading: isLoadingShopifyState } =
    useShopifyAppStoreControllerGetInstallState(shopifyState ?? '', {
      query: { enabled: !!shopifyState },
    });

  // Pre-fill email from Shopify shop as a convenience. Field stays editable;
  // user can clear it / use a different email. If they already have an account,
  // the "Sign in instead" alert below offers that path.
  // Loads GA4 so a new account's sign_up is credited to the marketing page.
  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    if (!shopifyInstallState) return;
    setEmail((current) => current || shopifyInstallState.email);
  }, [shopifyInstallState]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password || !confirmPassword) {
      toast.error(t('toasts.fillAllFields'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('toasts.passwordsMismatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('toasts.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      const result = await authControllerSignup({
        email,
        password,
        betaCode: promoCode || undefined,
        affiliateCode: affiliateCode || undefined,
        // Ties the pending Shopify install to the account server-side, so
        // provisioning survives the confirmation email being opened in a
        // different browser (where localStorage below is empty).
        shopifyStateToken: shopifyState || undefined,
      }) as any;

      // The account exists now (pending email confirmation). Fire here rather
      // than on /auth/confirm: this is still the session that came from the
      // marketing page, while the confirmation link often opens elsewhere.
      trackSignUp('email');

      // Store Shopify state for post-auth provisioning
      if (shopifyState) {
        localStorage.setItem('pendingShopifyState', shopifyState);
      }

      // Store promo code for billing step
      if (promoCode) {
        localStorage.setItem('pendingPromoCode', promoCode);
      }

      // Stash a post-onboarding redirect (e.g. back to /figma-auth?code=...)
      // The onboarding page consumes this when onboarding completes.
      if (redirectTo) {
        localStorage.setItem('pendingPostOnboardingRedirect', redirectTo);
      }

      // Dev mode: API returns tokens, auto-sign-in and go to onboarding
      if (result.accessToken && result.refreshToken) {
        await useAuthStore.getState().setTokensAndFetchUser(result.accessToken, result.refreshToken);
        toast.success(t('signUp.success'));
        navigate({ to: paths.auth.onboarding });
        return;
      }

      // Production: show email confirmation UI
      setEmailSent(true);
    } catch (err: any) {
      console.error('Sign up error:', err);
      const message = err?.message || t('signUp.error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setLoading(true);
    try {
      const { accessToken, refreshToken, isNewUser } = await authControllerGoogleSignin({ idToken });
      if (isNewUser) trackSignUp('google');
      await useAuthStore.getState().setTokensAndFetchUser(accessToken, refreshToken);
      toast.success(t('signUp.success'));

      if (shopifyState) localStorage.setItem('pendingShopifyState', shopifyState);
      if (promoCode) localStorage.setItem('pendingPromoCode', promoCode);
      if (redirectTo) localStorage.setItem('pendingPostOnboardingRedirect', redirectTo);
      await queryClient.invalidateQueries({ queryKey: ['/organizations'] });

      // A returning Google user with completed onboarding goes straight to the
      // dashboard; everyone else (new account, or mid-onboarding) continues
      // through onboarding. Shopify context always routes to onboarding so its
      // provisioning runs.
      if (!shopifyState) {
        try {
          const status = await authControllerGetOnboardingStatus();
          if (status.status === 'completed') {
            navigate({ to: paths.dashboard.root });
            return;
          }
        } catch {
          // fall through to onboarding
        }
      }
      navigate({ to: paths.auth.onboarding });
    } catch (err: any) {
      console.error('Google sign up error:', err);
      toast.error(err?.message || t('signUp.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setEmailSent(false);
    setPassword('');
    setConfirmPassword('');
  };

  const hasShopifyContext = !!shopifyState && !!shopifyInstallState;

  if (shopifyState && isLoadingShopifyState) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t('signUp.loadingShopify')}
          </Typography>
        </Box>
    );
  }

  return (
    <>
      {emailSent ? (
        <CheckEmailConfirmation email={email} onBack={handleBack} />
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
              {t('signUp.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasShopifyContext
                ? t('signUp.subtitleShopify', { org: shopifyInstallState.organizationName })
                : t('signUp.subtitle')}
            </Typography>
          </Box>

          {hasShopifyContext && (
            <Alert
              severity="info"
              sx={{ mb: 3 }}
              action={
                <Button
                  component={RouterLink}
                  href={`${paths.auth.signIn}?shopify_state=${shopifyState}`}
                  size="small"
                  variant="outlined"
                >
                  {t('links.signIn')}
                </Button>
              }
            >
              {t('signUp.shopifyLinkInfo')}
            </Alert>
          )}

          <Stack component="form" spacing={2.5} onSubmit={handleSignUp}>
            <TextField
              fullWidth
              label={t('fields.emailAddress')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="email"
              required
              helperText={hasShopifyContext ? t('signUp.shopifyEmailHelp') : undefined}
            />

            <TextField
              fullWidth
              label={t('fields.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={t('fields.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ py: 1.4 }}
            >
              {loading ? t('signUp.submitting') : t('signUp.submit')}
            </Button>

            <Divider sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              {t('signUp.or', 'or')}
            </Divider>

            <GoogleSignInButton onCredential={handleGoogleCredential} text="signup_with" />

            <LegalConsentNotice />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('signUp.haveAccount')}{' '}
                <Link
                  component={RouterLink}
                  href={
                    shopifyState
                      ? `${paths.auth.signIn}?shopify_state=${shopifyState}`
                      : redirectTo
                        ? `${paths.auth.signIn}?redirect=${encodeURIComponent(redirectTo)}`
                        : paths.auth.signIn
                  }
                  sx={{ cursor: 'pointer', fontWeight: 600 }}
                >
                  {t('links.signIn')}
                </Link>
              </Typography>
            </Box>
          </Stack>
        </>
      )}
    </>
  );
}
